import type { EChartsCoreOption } from 'echarts/core';
import { formatDateTime } from '../../shared/date-time';
import type { AnalysisSessionDataset } from './analysis-types';
import { humanize, readObject } from './format';
import type { AnalyticsEventRecord } from './types';

export type TrendValueMode = 'absolute' | 'delta';

export interface TrendDefinition {
  key: string;
  label: string;
  color: string;
  read: (event: AnalyticsEventRecord) => number | null;
}

const SESSION_COLORS = ['#86a8ff', '#72d7ae', '#f0c078', '#d696ff'];

export function buildTrendOption(
  datasets: AnalysisSessionDataset[],
  metrics: TrendDefinition[],
  comparison: boolean,
  metricKey: string,
  valueMode: TrendValueMode,
): EChartsCoreOption {
  const selectedMetric = metrics.find((metric) => metric.key === metricKey) ?? metrics[0];
  const series = comparison && selectedMetric !== undefined
    ? buildComparisonSeries(datasets, selectedMetric, valueMode)
    : buildSingleSessionSeries(datasets[0], metrics);
  return {
    backgroundColor: 'transparent',
    color: comparison ? SESSION_COLORS : metrics.map(readMetricColor),
    tooltip: { trigger: 'axis', formatter: formatTrendTooltip },
    legend: { type: 'scroll', textStyle: { color: '#8b99ae' } },
    grid: { left: 18, right: 20, top: 55, bottom: 28, containLabel: true },
    xAxis: { type: 'value', min: 0, axisLabel: { color: '#70809a', formatter: formatElapsedAxis }, axisLine: { lineStyle: { color: '#2a3850' } }, splitLine: { show: false } },
    yAxis: { type: 'value', axisLabel: { color: '#70809a' }, splitLine: { lineStyle: { color: '#202c3d' } } },
    series,
  };
}

export function buildResourceOption(datasets: AnalysisSessionDataset[], resourceKey: string): EChartsCoreOption {
  const series = [];
  for (const dataset of datasets) {
    const data = [];
    for (const event of dataset.events) {
      data.push({ value: [event.elapsedMs, readResourceValue(event, resourceKey)], analyticsEventId: event.eventId, clientTime: event.clientTime });
    }
    series.push({ name: `Session #${dataset.summary.sessionIndex}`, type: 'line', showSymbol: dataset.events.length < 30, smooth: true, data });
  }
  return {
    backgroundColor: 'transparent', color: SESSION_COLORS,
    tooltip: { trigger: 'axis', formatter: formatTrendTooltip },
    legend: { type: 'scroll', textStyle: { color: '#8b99ae' } },
    grid: { left: 10, right: 12, top: 45, bottom: 20, containLabel: true },
    xAxis: { type: 'value', min: 0, axisLabel: { color: '#70809a', formatter: formatElapsedAxis }, splitLine: { show: false } },
    yAxis: { type: 'value', axisLabel: { color: '#70809a' }, splitLine: { lineStyle: { color: '#202c3d' } } },
    series,
  };
}

export function buildConstructionOption(datasets: AnalysisSessionDataset[], eventKeys: string[]): EChartsCoreOption {
  const series = [];
  for (const dataset of datasets) {
    const counts = countByEventKey(dataset.events);
    series.push({ name: `Session #${dataset.summary.sessionIndex}`, type: 'bar', data: eventKeys.map((key) => counts[key] ?? 0), barMaxWidth: 34, itemStyle: { borderRadius: [5, 5, 0, 0] } });
  }
  return {
    backgroundColor: 'transparent', color: SESSION_COLORS,
    tooltip: { trigger: 'axis' },
    legend: { type: 'scroll', textStyle: { color: '#8b99ae' } },
    grid: { left: 18, right: 18, top: 55, bottom: 28, containLabel: true },
    xAxis: { type: 'category', data: eventKeys.map(humanize), axisLabel: { color: '#70809a', rotate: 18 }, axisLine: { lineStyle: { color: '#2a3850' } } },
    yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#70809a' }, splitLine: { lineStyle: { color: '#202c3d' } } },
    series,
  };
}

function buildSingleSessionSeries(dataset: AnalysisSessionDataset | undefined, metrics: TrendDefinition[]): unknown[] {
  if (dataset === undefined) return [];
  const series = [];
  for (const metric of metrics) {
    series.push({
      name: metric.label,
      type: 'line',
      showSymbol: dataset.events.length < 30,
      smooth: true,
      data: buildTrendData(dataset.events, metric, 'absolute', 0),
    });
  }
  return series;
}

function buildComparisonSeries(datasets: AnalysisSessionDataset[], metric: TrendDefinition, valueMode: TrendValueMode): unknown[] {
  const series = [];
  for (const dataset of datasets) {
    const baseline = findBaseline(dataset.events, metric);
    series.push({
      name: `Session #${dataset.summary.sessionIndex}`,
      type: 'line',
      showSymbol: dataset.events.length < 30,
      smooth: true,
      data: buildTrendData(dataset.events, metric, valueMode, baseline),
    });
  }
  return series;
}

function buildTrendData(events: AnalyticsEventRecord[], metric: TrendDefinition, valueMode: TrendValueMode, baseline: number | null): unknown[] {
  const data = [];
  for (const event of events) {
    const absoluteValue = metric.read(event);
    const value = absoluteValue === null || (valueMode === 'delta' && baseline === null)
      ? null
      : absoluteValue - (valueMode === 'delta' ? baseline ?? 0 : 0);
    data.push({ value: [event.elapsedMs, value], analyticsEventId: event.eventId, clientTime: event.clientTime });
  }
  return data;
}

function findBaseline(events: AnalyticsEventRecord[], metric: TrendDefinition): number | null {
  for (const event of events) {
    const value = metric.read(event);
    if (value !== null) return value;
  }
  return null;
}

function countByEventKey(events: AnalyticsEventRecord[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const event of events) counts[event.eventKey] = (counts[event.eventKey] ?? 0) + 1;
  return counts;
}

function formatTrendTooltip(parameters: unknown): string {
  if (!Array.isArray(parameters) || parameters.length === 0) return '';
  const context = readTooltipContext(parameters[0]);
  const heading = context === null
    ? ''
    : formatElapsedDetailed(context.elapsedMs);
  const rows = [];
  for (const parameter of parameters) {
    if (typeof parameter !== 'object' || parameter === null) continue;
    const name = 'seriesName' in parameter ? String(parameter.seriesName) : 'Value';
    const value = readTooltipValue(parameter);
    const marker = 'marker' in parameter ? String(parameter.marker) : '';
    const rowContext = readTooltipContext(parameter);
    const timestamp = rowContext === null ? '' : ` <small>· ${escapeHtml(formatDate(rowContext.clientTime))}</small>`;
    rows.push(`${marker}${escapeHtml(name)}: <strong>${escapeHtml(formatNumber(value))}</strong>${timestamp}`);
  }
  return [escapeHtml(heading), ...rows].join('<br>');
}

function readTooltipContext(parameter: unknown): { elapsedMs: number; clientTime: string } | null {
  if (typeof parameter !== 'object' || parameter === null || !('data' in parameter)) return null;
  const data = parameter.data;
  if (typeof data !== 'object' || data === null || !('value' in data) || !Array.isArray(data.value) || !('clientTime' in data)) return null;
  const elapsedMs = Number(data.value[0]);
  return Number.isFinite(elapsedMs) && typeof data.clientTime === 'string' ? { elapsedMs, clientTime: data.clientTime } : null;
}

function readTooltipValue(parameter: object): number {
  if (!('value' in parameter) || !Array.isArray(parameter.value)) return 0;
  const value = parameter.value[1];
  return typeof value === 'number' ? value : Number(value) || 0;
}

function readMetricColor(metric: TrendDefinition): string { return metric.color; }
function readResourceValue(event: AnalyticsEventRecord | undefined, key: string): number | null { const value = readObject(event, 'resources')[key]; return typeof value === 'number' && Number.isFinite(value) ? value : null; }
function formatElapsedAxis(value: string | number): string { return formatElapsed(Number(value)); }
function formatElapsed(milliseconds: number): string { const totalSeconds = Math.floor(milliseconds / 1000); const minutes = Math.floor(totalSeconds / 60); return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`; }
function formatElapsedDetailed(milliseconds: number): string { const totalSeconds = Math.floor(milliseconds / 1000); const minutes = Math.floor(totalSeconds / 60); return minutes < 60 ? `${minutes}m ${totalSeconds % 60}s` : `${Math.floor(minutes / 60)}h ${minutes % 60}m ${totalSeconds % 60}s`; }
function formatDate(value: string): string { return formatDateTime(value, 'second'); }
function formatNumber(value: number): string { return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value); }
function escapeHtml(value: string): string { return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#039;'); }
